/** Check-in page (Admissions and admins): today's events, walk-ins, and past no-shows. */
import { api } from "../api";
import { S, isAdmin, on, render } from "../state";
import { $, I, closeSheet, esc, fullName, sized, toast } from "../ui";
import { fmtDay, fmtTime } from "../../shared/constants";
import type { EventView, SignupView } from "../../shared/types";
import { colorOf, put, shortTitle } from "../events";

type NoShow = { id: number; eventId: number; kind: string; shadow: boolean; title: string; type: string; date: string; start: string; end: string; first: string; last: string };
type Person = { id: number; first: string; last: string; pa: boolean; sa: boolean };

const CI: { day: string | null; events: EventView[] | null; loadedFor: string; noshows: NoShow[] | null; people: Person[] | null } = {
  day: null, events: null, loadedFor: "", noshows: null, people: null,
};

export function resetCheckin() {
  CI.events = null;
  CI.loadedFor = "";
  CI.noshows = null;
  CI.people = null;
}

const today = () => S.me!.today;
const shownDay = () => (CI.day && CI.day < today() && isAdmin() ? CI.day : today());

async function load() {
  const day = shownDay();
  if (CI.loadedFor === day) return;
  CI.loadedFor = day;
  try {
    const [d, ns] = await Promise.all([
      api<{ day: string; events: EventView[] }>(`/api/checkin?day=${day}`),
      day === today() ? api<NoShow[]>("/api/checkin/noshows") : Promise.resolve(CI.noshows),
    ]);
    CI.events = d.events;
    CI.noshows = ns;
  } catch (e) {
    toast((e as Error).message);
    CI.events = [];
  }
  render();
}

/** Today's events with sign-ups, for the Home card. Loads once per visit to Home. */
export function todaysCheckins() {
  if (CI.loadedFor !== today()) {
    CI.day = null;
    void load();
  }
  return (CI.events || []).filter((e) => e.signups.length);
}
export const noShowCount = () => CI.noshows?.length ?? null;

const PHONE =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z"/></svg>';
const callBtn = (s: SignupView) =>
  s.phone ? `<a class="callbtn" href="tel:+1${s.phone.replace(/\D/g, "")}" aria-label="Call ${esc(fullName(s))} at ${esc(s.phone)}" title="Call ${esc(s.phone)}">${PHONE}</a>` : "";
const here = (e: EventView) => `${e.signups.filter((s) => s.checkedIn).length} of ${e.signups.length} here`;

function eventCard(e: EventView, past: boolean) {
  const list = [...e.signups].sort((a, b) => fullName(a).localeCompare(fullName(b), "en", { sensitivity: "base" }));
  return `<div class="card" style="border-left:8px solid ${colorOf(e)}"><div class="row between wrap"><div><h2>${esc(e.title)}</h2><div class="muted small">${past ? fmtDay(e.date) : "Today"} · ${fmtTime(e.start)} to ${fmtTime(e.end)}</div></div><span class="chip blue" id="cic${e.id}">${here(e)}</span></div>
  <ul class="list" style="margin-top:10px">${
    list
      .map(
        (s) => `<li class="row between"><label class="row" style="cursor:pointer"><input type="checkbox" class="check" ${s.checkedIn ? "checked" : ""} data-change="ci-tick" data-eid="${e.id}" data-sid="${s.id}" data-name="${esc(fullName(s))}"><span><b>${esc(fullName(s))}</b>${s.shadow ? " (Shadow)" : ""}${s.walkin ? ' <span class="chip">Walk-In</span>' : ""}</span></label><span class="row" style="gap:8px">${callBtn(s)}<span class="chip ${s.kind === "SA" ? "red" : "blue"}">${s.kind}</span></span></li>`,
      )
      .join("") || '<li class="muted">No one signed up for this event</li>'
  }</ul>
  <div id="wk${e.id}" style="margin-top:12px"><button class="btn ghost sm" data-act="wk-open" data-eid="${e.id}">${sized(I.plus, 14)} Add a Walk-In</button></div></div>`;
}

export function checkinView() {
  const day = shownDay();
  const past = day < today();
  if (CI.loadedFor !== day) void load();
  const events = CI.events;
  const ns = CI.noshows || [];
  const head = past
    ? `<div class="row between wrap"><h1>Check-ins for ${fmtDay(day, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</h1><button class="btn ghost sm" data-act="ci-today">Back to Today</button></div>`
    : "";
  return `<div class="stack">${head}
  <p class="muted small" style="margin:${past ? "-6px" : "0"} 0 0">${past ? "What was recorded that day. As an admin you can still correct it." : "Tick a name to check them in. It saves right away, and so does unticking."}</p>
  ${!events ? '<p class="muted">Loading…</p>' : events.map((e) => eventCard(e, past)).join("") || `<p class="muted">Nothing on the calendar ${past ? "that day" : "today"}.</p>`}
  ${
    past
      ? ""
      : `<div class="card"><div class="row between"><h2>Past No-Shows</h2><span class="chip ${ns.length ? "warn" : "ok"}">${ns.length ? ns.length + " to review" : "All clear"}</span></div>
   <p class="muted small" style="margin:4px 0 10px">Anyone not checked in by the end of the day lands here until you sort it out.</p>
   ${
     ns.length
       ? `<ul class="list">${ns
           .map(
             (x) => `<li class="row between wrap" style="gap:8px"><div><b>${esc(fullName(x))}</b>${x.shadow ? " (Shadow)" : ""}<div class="muted small">${esc(shortTitle(x.title))} · ${fmtDay(x.date)}, ${fmtTime(x.start)} to ${fmtTime(x.end)}</div></div><div class="row"><button class="btn sm" data-act="ns-resolve" data-sid="${x.id}" data-there="1" data-name="${esc(fullName(x))}">Was There</button><button class="btn ghost sm" data-act="ns-resolve" data-sid="${x.id}" data-there="0" data-name="${esc(fullName(x))}">No-Show</button></div></li>`,
           )
           .join("")}</ul>`
       : '<p class="muted" style="margin:0">Nothing to review.</p>'
   }</div>`
  }</div>`;
}

/** From the event pop-up: today for staff, past days for admins. */
export function goCheckin(day: string) {
  closeSheet();
  CI.day = day === today() ? null : day;
  if (location.hash === "#checkin") render();
  else location.hash = "#checkin";
  scrollTo(0, 0);
}

function replaceEvent(e: EventView) {
  put(e);
  if (CI.events) CI.events = CI.events.map((x) => (x.id === e.id ? e : x));
}

on({
  "ci-today": () => {
    CI.day = null;
    render();
  },
  "ci-tick": async (el) => {
    const box = el as HTMLInputElement;
    const name = el.dataset.name!;
    try {
      const e = await api<EventView>(`/api/checkin/${el.dataset.sid}`, { method: "PUT", body: { on: box.checked } });
      replaceEvent(e);
      const c = $(`#cic${e.id}`);
      if (c) c.textContent = here(e);
      toast(box.checked ? `${name} has been checked-in` : `${name} checked-in removed`);
    } catch (err) {
      box.checked = !box.checked;
      toast((err as Error).message);
    }
  },
  "wk-open": async (el) => {
    const id = el.dataset.eid!;
    $(`#wk${id}`)!.innerHTML = `<label class="fld" for="wkq${id}">Add a Walk-In</label><div class="row" style="gap:6px"><input type="text" id="wkq${id}" placeholder="Start typing a name" autocomplete="off" data-input="wk-search" data-eid="${id}"><button class="btn ghost sm" data-act="wk-cancel">Cancel</button></div><ul class="list wkres" id="wkr${id}"></ul>`;
    ($(`#wkq${id}`) as HTMLInputElement).focus();
    if (!CI.people) CI.people = await api<Person[]>("/api/events/people").catch(() => []);
  },
  "wk-cancel": () => render(),
  "wk-search": (el) => {
    const id = Number(el.dataset.eid);
    const q = (el as HTMLInputElement).value.trim().toLowerCase();
    const e = CI.events?.find((x) => x.id === id);
    if (!e) return;
    const taken = new Set(e.signups.map((s) => s.userId));
    const hits = q
      ? (CI.people || [])
          .filter((p) => (p.pa || p.sa) && !taken.has(p.id) && fullName(p).toLowerCase().split(" ").concat(fullName(p).toLowerCase()).some((w) => w.startsWith(q)))
          .slice(0, 8)
      : [];
    $(`#wkr${id}`)!.innerHTML =
      hits
        .map((p) => {
          const k = p.sa && !p.pa ? "SA" : "PA";
          return `<li><button class="wkhit" data-act="wk-pick" data-eid="${id}" data-uid="${p.id}" data-kind="${k}" data-both="${p.pa && p.sa ? 1 : 0}" data-name="${esc(fullName(p))}"><b>${esc(fullName(p))}</b><span class="chip ${k === "SA" ? "red" : "blue"}">${p.pa && p.sa ? "PA, SA" : k}</span></button></li>`;
        })
        .join("") || (q ? '<li class="muted small" style="padding:8px 0">No matches</li>' : "");
  },
  "wk-pick": (el) => {
    const { eid, uid, kind, name, both } = el.dataset;
    const e = CI.events?.find((x) => x.id === Number(eid));
    const lead = e && /tour/i.test(e.title) ? "Tour Lead" : "Regular Spot";
    $(`#wk${eid}`)!.innerHTML = `<div class="row wrap" style="gap:6px"><span class="row" style="gap:8px;flex:1 1 180px"><b>${name}</b></span>${
      both === "1" ? `<select class="f" id="wkk${eid}" style="width:auto" aria-label="Role"><option value="PA">PA</option><option value="SA">SA</option></select>` : `<span class="chip ${kind === "SA" ? "red" : "blue"}">${kind}</span><input type="hidden" id="wkk${eid}" value="${kind}">`
    }<select class="f" id="wks${eid}" style="width:auto" aria-label="Spot"><option value="0">${lead}</option><option value="1">Shadow</option></select><button class="btn sm" data-act="wk-add" data-eid="${eid}" data-uid="${uid}" data-name="${name}">Add and Check In</button><button class="btn ghost sm" data-act="wk-open" data-eid="${eid}">Change</button></div>`;
  },
  "wk-add": async (el) => {
    const { eid, uid, name } = el.dataset;
    const kind = ($(`#wkk${eid}`) as HTMLInputElement).value;
    const shadow = ($(`#wks${eid}`) as HTMLSelectElement).value === "1";
    try {
      replaceEvent(await api<EventView>(`/api/events/${eid}/signups`, { body: { userId: Number(uid), kind, shadow, walkin: true } }));
      render();
      toast(`${name} added and checked in`);
    } catch (err) {
      toast((err as Error).message);
    }
  },
  "ns-resolve": async (el) => {
    const there = el.dataset.there === "1";
    const name = el.dataset.name!;
    try {
      await api(`/api/checkin/${el.dataset.sid}/resolve`, { body: { there } });
      CI.noshows = (CI.noshows || []).filter((x) => x.id !== Number(el.dataset.sid));
      render();
      toast(there ? `${name} marked as there` : `No-show recorded for ${name}`);
    } catch (err) {
      toast((err as Error).message);
    }
  },
  "go-checkin": (el) => goCheckin(el.dataset.day!),
});
